import { useState, useEffect, useCallback, useRef } from 'react';
import api from 'services/api';
import { useTenant } from 'context/TenantContext';
import { exportPagePdf } from 'utils/exportPdf';
import AssistantComptable from 'components/shared/AssistantComptable';
import { aujourdHui } from '../../../../utils/jour';

const JOURNAUX = [
  { code: '', label: 'Tous les journaux' },
  { code: 'JV', label: 'Journal des ventes' },
  { code: 'JA', label: 'Journal des achats' },
  { code: 'JB', label: 'Journal de banque' },
  { code: 'JC', label: 'Journal de caisse' },
  { code: 'JOD', label: 'Opérations diverses' },
];

export default function JournauxPage() {
  // Identite du TENANT pour l'en-tete/pied des exports PDF.
  // Sans raison sociale, NIF et RCCM, l'export est REFUSE (cf. utils/exportPdf.js).
  const tenant = useTenant();
  const [data, setData] = useState({ ecritures: [], totalDebit: 0, totalCredit: 0 });
  const [loading, setLoading] = useState(true);
  const [journal, setJournal] = useState('');
  const [debut, setDebut] = useState(() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`; });
  const [fin, setFin] = useState(() => aujourdHui());
  const printRef = useRef();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      let url = `/syscohada/journaux?debut=${debut}&fin=${fin}`;
      if (journal) url += `&journal=${journal}`;
      const r = await api.get(url);
      setData(r.data);
    } catch {} finally { setLoading(false); }
  }, [journal, debut, fin]);

  useEffect(() => { load(); }, [load]);

  const journalLabel = JOURNAUX.find(j => j.code === journal)?.label || 'Tous les journaux';

  return (
    <div>
      <div className="gss-toolbar" style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <select className="gss-input" value={journal} onChange={e => setJournal(e.target.value)} style={{ width: 200 }}>
            {JOURNAUX.map(j => <option key={j.code} value={j.code}>{j.label}</option>)}
          </select>
          <input type="date" className="gss-input" value={debut} onChange={e => setDebut(e.target.value)} style={{ width: 150 }} />
          <input type="date" className="gss-input" value={fin} onChange={e => setFin(e.target.value)} style={{ width: 150 }} />
        </div>
        <button className="gss-btn gss-btn-primary" onClick={() => exportPagePdf(`${journalLabel} — ${debut} au ${fin}`, printRef.current, tenant)}>Exporter PDF</button>
      </div>

      <div className="stats-grid stats-grid--3">
        <div className="stat-card"><div className="stat-card-header"><span className="stat-card-label">Écritures</span></div><div className="stat-card-value">{data.ecritures.length}</div></div>
        <div className="stat-card"><div className="stat-card-header"><span className="stat-card-label">Total débits</span></div><div className="stat-card-value" style={{ color: '#1565c0' }}>{Number(data.totalDebit).toLocaleString()}</div><div className="stat-card-sub">FCFA</div></div>
        <div className="stat-card"><div className="stat-card-header"><span className="stat-card-label">Total crédits</span></div><div className="stat-card-value" style={{ color: '#2e7d32' }}>{Number(data.totalCredit).toLocaleString()}</div><div className="stat-card-sub">FCFA</div></div>
      </div>

      <div className="content-card" ref={printRef}>
        <div className="content-card-title">{journalLabel}</div>
        {loading ? <p style={{ textAlign: 'center', padding: '2rem', color: 'var(--gss-text-light)' }}>Chargement...</p> : (
          <div className="gss-table-wrap">
            <table className="gss-table">
              <thead><tr><th>N° pièce</th><th>Date</th><th>Libellé</th><th>Compte débité</th><th>Compte crédité</th><th style={{ textAlign: 'right' }}>Montant</th></tr></thead>
              <tbody>
                {data.ecritures.length === 0 ? (
                  <tr><td colSpan={6} style={{ textAlign: 'center', padding: '2rem', color: '#888' }}>Aucune écriture sur cette période</td></tr>
                ) : data.ecritures.map(e => (
                  <tr key={e.id}>
                    <td style={{ fontFamily: 'monospace', fontSize: 11 }}>{e.numero_piece}</td>
                    <td>{e.date_ecriture?.split('T')[0]}</td>
                    <td style={{ fontWeight: 600 }}>{e.libelle}</td>
                    <td><span style={{ fontFamily: 'monospace' }}>{e.compte_debit}</span> <span style={{ fontSize: 11, color: '#888' }}>{e.debit_libelle}</span></td>
                    <td><span style={{ fontFamily: 'monospace' }}>{e.compte_credit}</span> <span style={{ fontSize: 11, color: '#888' }}>{e.credit_libelle}</span></td>
                    <td style={{ textAlign: 'right', fontWeight: 600 }}>{Number(e.montant).toLocaleString()} F</td>
                  </tr>
                ))}
              </tbody>
              {data.ecritures.length > 0 && (
                <tfoot>
                  <tr style={{ fontWeight: 700, borderTop: '2px solid #8B1A1A' }}>
                    <td colSpan={5}>TOTAUX</td>
                    <td style={{ textAlign: 'right' }}>{Number(data.totalDebit).toLocaleString()} F</td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        )}
        <div style={{ padding: '8px 16px', fontSize: 11, color: data.totalDebit === data.totalCredit ? '#2e7d32' : '#DC2626', fontWeight: 700, textAlign: 'right' }}>
          {data.totalDebit === data.totalCredit ? 'Équilibré — Débits = Crédits' : 'ATTENTION : Déséquilibre détecté'}
        </div>
      </div>

      <AssistantComptable />
    </div>
  );
}
